<template>
  <!-- We should extract "validate" as a standalone function in order to not use the BaseForm wrapper -->
  <component :is="asForm ? BaseForm : 'div'" ref="attributeSelectionFormRef" :form="attributeSelection">
    <section
      v-for="({ groupedOptions, label, type }, i) in variantAttributes"
      :key="type"
      :class="{ '<lg:op-0': showOnlyFirstSelectorOnMobile && i !== 0, 'mt-6': i !== 0 }"
      :data-scroll-el="`${type}Pickers`"
      :data-test-id="`${type}-groups`"
    >
      <div :class="{ 'flex items-center space-x-2': showBadge }">
        <div v-if="showBadge" class="h-4 w-4 flex justify-center rounded-full bg-grey-10">
          <span class="text-xs c-white fw-bold">{{ i + 1 }}</span>
        </div>
      </div>
      <vf-form-error v-if="type !== 'color' && error && !attributeSelection[type]" class="mt-1">
        {{ product.productType === 'Digital' ? $t.selectCardAmountError : $t.selectSizeError }}
      </vf-form-error>
      <div
        v-for="({ options }, j) in groupedOptions"
        :key="j"
        :data-test-id="`${type}-groups-item`"
      >
        <fieldset v-if="type === 'color'" class="space-y-2">
          <legend class="sr-only">
            {{ label }}
          </legend>
          <vf-carousel
            class-container="gap-1 p-px -m-px <lg:w-screen <lg:scroll-px-4 <lg:px-4 <lg:-mx-4 lg:wrap"
            class-controls="!hidden"
            @pointerdown="(e) => e.stopPropagation()"
          >
            <vf-color-picker
              v-for="({ available, id, label: optionLabel, price, swatch, url, value }, k) in
                options.sort((a, b) => a.id === firstSelectedProduct ? -1 : b.id === firstSelectedProduct ? 1 : 0)"
              :key="optionLabel"
              :active="!pending && getIsActive(value, id)"
              :class="{ 'lg:hidden': maxColorOptions < (k + 1) }"
              :color="swatch?.color"
              :loading="getIsPending(value, id, pending)"
              :name="optionLabel"
              :sale="$feature.showOnSaleSwatchIndicatorOnPDP && price.lowCurrent < price.lowOriginal"
              :size="colorPickerSize"
              :thumbnail="swatch?.image"
              :unavailable="!isComingSoon && (!available && !pending)"
              :variant="colorPickerShape"
              @click="selectNewColor(enableColorAsAttribute ? value : id, url, optionLabel)"
            />
            <base-link
              v-if="$feature.showCustomizeOnPDP && product.customsExternalUrl"
              v-style="customizeContainer"
              class="body-4 flex flex-col items-center justify-center rounded bg-brand-2 @hactive:bg-grey-70 <lg:text-micro"
              :to="product.customsExternalUrl"
            >
              <vf-icon name="customs" size="md" />
              <span>{{ $t.customize }}</span>
            </base-link>
            <base-button
              v-if="maxColorOptions < options.length"
              v-style="container"
              class="flex flex-col items-center justify-center rounded bg-brand-2 <lg:hidden @hactive:bg-grey-70"
              @click="colorOptionsExpanded = true"
            >
              <span class="subtitle-4">+{{ options.length - maxColorOptions }}</span>
              <span class="body-4">{{ $t.more }}</span>
            </base-button>
            <base-button
              v-else-if="colorOptionsExpanded"
              v-style="container"
              class="flex flex-col items-center justify-center rounded bg-brand-2 <lg:hidden @hactive:bg-grey-70"
              @click="colorOptionsExpanded = false"
            >
              <span class="body-4 ws-pre text-center">{{ $t.showLess }}</span>
            </base-button>
          </vf-carousel>
          <p class="text-xs">
            {{ attributeSelectionLabels[type] }}
          </p>
        </fieldset>
        <fieldset v-else>
          <legend class="subtitle-4" :class="{ 'sr-only': !$feature.pdpShowDropdownLabel }">
            {{ label }}
          </legend>
          <base-form-field
            v-slot="{ attrs: { onChange } }"
            :name="type"
            :rule="validateRequired()"
            :selector="`${type}Pickers`"
          >
            <vf-size-select
              v-model="attributeSelection[type]"
              class="mt-2"
              :unavailable="!pending && !options.find(({ value }) => value === attributeSelection[type])?.available"
              @change="onChange"
              @click="PanelVariantSelection.open({
                attribute: variantAttributes[i],
                enableReserveInStore: isRobisOnly,
              })"
            >
              {{ attributeSelectionLabels[type] || $t.selectAttribute[type] }}
            </vf-size-select>
          </base-form-field>
        </fieldset>
      </div>
    </section>
  </component>

  <panel-variant-selection :size="{ sm: 'sm', lg: 'full' }" />
</template>

<script lang="ts" setup>
import { BaseForm } from '#components'
import type { BaseForm as BaseFormType } from '#components'
import type { AttributeSelection, ColorSwatchShape, ProductAttributeType, ProductExtended, SelectedVariant } from '#types/product'
import type { Responsive } from '#types/common'
import type { SizesSubUnion } from '#types/sizes'
import type { ColorPickerStyles } from '#types/config/components/vf/colorPicker'

const props = defineProps<{
  /*
  * variants, showNotifyMe, productInventory props are added only for consistency with other brands
  * They aren't used in the component
  * but allows to avoid situations with incorrect props resolution when rendering the DOM
  */
  attributeSelectionLabels: Record<string, string>
  colorSize?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  colorSwatch?: ColorSwatchShape
  error?: boolean
  isComingSoon?: boolean
  pending?: boolean
  product: ProductExtended
  selectedVariant?: SelectedVariant | null
  showBadge?: boolean
  showOnlyFirstSelectorOnMobile?: boolean
  asForm?: boolean
  showNotifyMe?: boolean
  variants?: any
}>()

const emit = defineEmits<{
  selectColor: [id: string, url: string, optionLabel: string]
  selectSize: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
}>()

const { enableColorAsAttribute, swatches } = useAppConfig().pages.pdp || {}
const { brandStyles } = useAppConfig().components.vf.colorPicker
const { $feature, $t } = useNuxtApp()

const attributeSelectionFormRef = defineModel<InstanceType<typeof BaseFormType> | null>('formRef', { required: false })
const attributeSelection = defineModel<AttributeSelection>({ required: true })

const PanelVariantSelection = createPanel('variant-selection')

const selectedProductId = ref()
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && props.product?.isRobisOnly)

const colorPickerShape = props.colorSwatch ?? swatches.variant
const colorPickerSize = props.colorSize ?? swatches.size

// When the user hits the PDP for the first time, we store the first selected product to show it as first on the color picker
const firstSelectedProduct = props.product.id

const variantAttributes = computed(() => props.isComingSoon
  ? props.product.attributes.filter((attribute) => attribute.type === 'color')
  : props.product.attributes
)

const { container } = Object.entries(brandStyles[colorPickerShape]).reduce((acc: ColorPickerStyles, [key, value]) => {
  acc[key] = {}

  for (const styleProp in value) {
    acc[key][styleProp] = replaceValues(isObject(colorPickerSize)
      ? colorPickerSize
      : { sm: colorPickerSize }, value[styleProp])
  }

  return acc
}, {})

const customizeSpan = $t.customize.length < 10 ? 1 : 2

const customizeContainer = {
  ...container,
  w: {
    sm: container?.w?.sm && `${(Number(container.w.sm.replace('rem', '')) * customizeSpan + 0.25 * (customizeSpan - 1))}rem`,
    md: container?.w?.md && `${(Number(container.w.md.replace('rem', '')) * customizeSpan + 0.25 * (customizeSpan - 1))}rem`,
    lg: container?.w?.lg && `${(Number(container.w.lg.replace('rem', '')) * customizeSpan + 0.25 * (customizeSpan - 1))}rem`
  }
}

const colorOptionsExpanded = ref(false)

const maxColorOptions = computed(() => {
  if (colorOptionsExpanded.value) return Number.POSITIVE_INFINITY

  return 9 - (($feature.showCustomizeOnPDP && props.product.customsExternalUrl) ? customizeSpan : 0)
})

const getIsActive = (value: string, id: string) =>
  enableColorAsAttribute ? attributeSelection.value.color === value : props.product.id === id

const getIsPending = (value: string, id: string, pending?: boolean) => {
  if (!pending) return

  return enableColorAsAttribute
    ? attributeSelection.value.color === value
    : selectedProductId.value === id
}

const selectNewColor = (id, url, optionLabel) => {
  selectedProductId.value = id
  emit('selectColor', id, url, optionLabel)
}

defineExpose({
  validate: () => attributeSelectionFormRef.value?.validate()
})
</script>
